import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { motion } from "framer-motion";
import {
  FaCloudUploadAlt,
  FaFile,
  FaTimes,
  FaArrowLeft,
  FaShieldAlt,
  FaUpload,
  FaSpinner,
  FaFilePdf,
  FaCheckCircle,
} from "react-icons/fa";
import toast from "react-hot-toast";
import { useFooter } from "../context/FooterContext";

const UploadPYQ = () => {
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subject: "",
    subjectCode: "",
    branch: "",
    semester: "",
    examYear: "",
    examType: "External",
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => setIsMobile(window.innerWidth <= 768);
  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const branches = ["CSE", "ISE", "ECE", "EEE", "MECH", "CIVIL", "AIML", "DS"];
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];
  const examTypes = ["Internal", "External", "CIE", "SEE"];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) =>
    (currentYear - i).toString(),
  );

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  const onDrop = (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile.size > 20 * 1024 * 1024) {
      toast.error("File size should be less than 20MB");
      return;
    }
    setFile(uploadedFile);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 1,
  });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a PDF file");
      return;
    }
    setUploading(true);
    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));
    data.append("file", file);
    try {
      const token = localStorage.getItem("token");
      await axios.post("/api/pyqs", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("PYQ uploaded successfully!");
      navigate("/pyqs");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to upload PYQ");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "calc(100vh - 200px)",
      }}
    >
      <div style={{ position: "relative", zIndex: 1 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#8b5cf6",
            background: "none",
            border: "none",
            cursor: "pointer",
            marginBottom: "1.5rem",
          }}
        >
          <FaArrowLeft /> Back
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
            borderRadius: "28px",
            padding: "2rem",
            marginBottom: "2rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "0.5rem",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                width: "50px",
                height: "50px",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaUpload size={24} color="white" />
            </div>
            <h1
              style={{
                fontSize: isMobile ? "1.8rem" : "2rem",
                fontWeight: "bold",
                color: "white",
                margin: 0,
              }}
            >
              Upload <span style={{ color: "#8b5cf6" }}>PYQ</span>
            </h1>
          </div>
          <p style={{ color: "rgba(255,255,255,0.9)", marginLeft: "3rem" }}>
            Upload previous year question papers for VTU students
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          style={{
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            padding: isMobile ? "1.5rem" : "2rem",
            borderRadius: "28px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <div
            {...getRootProps()}
            style={{
              border: `2px dashed ${isDragActive ? "#8b5cf6" : getBorderColor()}`,
              borderRadius: "20px",
              padding: "2rem",
              textAlign: "center",
              marginBottom: "2rem",
              cursor: "pointer",
              background: isDragActive
                ? isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)"
                : "transparent",
            }}
          >
            <input {...getInputProps()} />
            {file ? (
              <div>
                <FaFilePdf size={50} color="#ef4444" />
                <p style={{ color: getTextColor(), marginTop: "0.5rem" }}>
                  {file.name}
                </p>
                <p style={{ color: getTextSecondary(), fontSize: "0.85rem" }}>
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  style={{
                    marginTop: "1rem",
                    padding: "0.3rem 1rem",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "20px",
                    cursor: "pointer",
                  }}
                >
                  <FaTimes /> Remove
                </button>
              </div>
            ) : (
              <div>
                <FaCloudUploadAlt size={60} color="#8b5cf6" />
                <p style={{ color: getTextColor(), marginTop: "0.5rem" }}>
                  {isDragActive
                    ? "Drop the PDF here"
                    : "Drag & drop a PDF, or click to select"}
                </p>
                <p style={{ color: getTextSecondary(), fontSize: "0.85rem" }}>
                  Only PDF files (Max 20MB)
                </p>
              </div>
            )}
          </div>

          <div style={{ display: "grid", gap: "1.5rem" }}>
            <div>
              <label style={{ color: getTextSecondary() }}>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>
            <div>
              <label style={{ color: getTextSecondary() }}>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label style={{ color: getTextSecondary() }}>Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>
              <div>
                <label style={{ color: getTextSecondary() }}>
                  Subject Code *
                </label>
                <input
                  type="text"
                  name="subjectCode"
                  value={formData.subjectCode}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    background: getInputBackground(),
                    color: getTextColor(),
                    textTransform: "uppercase",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label style={{ color: getTextSecondary() }}>Branch *</label>
                <select
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select</option>
                  {branches.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ color: getTextSecondary() }}>Semester *</label>
                <select
                  name="semester"
                  value={formData.semester}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select</option>
                  {semesters.map((s) => (
                    <option key={s} value={s}>
                      Semester {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ color: getTextSecondary() }}>Exam Year *</label>
                <select
                  name="examYear"
                  value={formData.examYear}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ color: getTextSecondary() }}>Exam Type</label>
                <select
                  name="examType"
                  value={formData.examType}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  {examTypes.map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={uploading}
            style={{
              width: "100%",
              padding: "1rem",
              marginTop: "2rem",
              background: uploading
                ? "#94a3b8"
                : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "16px",
              fontSize: "1rem",
              fontWeight: 600,
              cursor: uploading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            {uploading ? (
              <>
                <FaSpinner style={{ animation: "spin 1s linear infinite" }} />{" "}
                Uploading...
              </>
            ) : (
              <>
                <FaCloudUploadAlt /> Upload PYQ
              </>
            )}
          </button>
        </motion.form>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default UploadPYQ;

